import { ReactNode } from 'react';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { cockpitTokens } from './cockpitTokens';
import {
  attentionOf,
  AttentionAction,
  AttentionRow,
  AttentionSource,
  AttentionTone,
} from './attention';
import { useHomeSources } from './homeSources';
import { usePullRequestLists } from './pullRequestLists';
import { CI_WIDGET_ANCHOR_ID, scrollToAnchor } from './anchors';

/**
 * Needs attention (DX-307): the open work the page can see, one action per row.
 *
 * Which rows, how many and in what order is decided in `attention.ts`; this
 * draws them, and draws the three states a list of work can be in besides
 * "here it is" — still being asked, partly asked, and nothing open — without
 * letting any of them look like the others (specs/needs-attention.md).
 *
 * MUI v4 only.
 */

const useStyles = makeStyles(theme => ({
  card: {
    background: cockpitTokens.color.surface,
    border: `1px solid ${cockpitTokens.color.border}`,
    borderRadius: cockpitTokens.radius.card,
    boxShadow: cockpitTokens.shadow.card,
    minWidth: 0,
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: cockpitTokens.color.text,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  count: {
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1,
    padding: '3px 7px',
    borderRadius: cockpitTokens.radius.pill,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
  },
  sources: {
    fontSize: '0.8rem',
    color: cockpitTokens.color.textFaint,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
    '&:last-child': { borderBottom: 'none' },
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap' as const,
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: 8,
    '& svg': { fontSize: 18 },
  },
  failure: {
    color: theme.palette.error.main,
    background: 'var(--ui-bg-muted)',
  },
  review: {
    color: cockpitTokens.color.brand,
    background: 'var(--color-brand-50)',
  },
  defect: {
    color: 'var(--ui-warn-text)',
    background: 'var(--ui-warn-bg)',
  },
  body: {
    flex: '1 1 0',
    minWidth: 0,
    // On a phone the action goes under the text rather than beside it: beside
    // it, a title had a third of the width and broke into three lines.
    [theme.breakpoints.down('xs')]: {
      flexBasis: 'calc(100% - 44px)',
    },
  },
  rowTitle: {
    fontSize: '0.92rem',
    fontWeight: 500,
    color: cockpitTokens.color.text,
    overflowWrap: 'anywhere' as const,
  },
  code: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.82rem',
    padding: '1px 5px',
    borderRadius: 4,
    border: `1px solid ${cockpitTokens.color.border}`,
    background: cockpitTokens.color.surfaceStrong,
  },
  detail: {
    fontSize: '0.8rem',
    color: cockpitTokens.color.textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  side: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 44,
    },
  },
  when: {
    fontSize: '0.78rem',
    color: cockpitTokens.color.textFaint,
    whiteSpace: 'nowrap' as const,
  },
  action: {
    fontSize: '0.8rem',
    fontWeight: 500,
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 'var(--ui-radius)',
    border: `1px solid ${cockpitTokens.color.border}`,
    background: cockpitTokens.color.surface,
    color: cockpitTokens.color.text,
    textDecoration: 'none',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': {
      borderColor: cockpitTokens.color.borderHover,
      textDecoration: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.borderHover}`,
      outlineOffset: 2,
    },
  },
  calm: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3, 2),
  },
  calmIcon: {
    color: theme.palette.success.main,
    flexShrink: 0,
  },
  calmTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: cockpitTokens.color.text,
  },
  calmText: {
    fontSize: '0.85rem',
    color: cockpitTokens.color.textMuted,
  },
  notes: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${cockpitTokens.color.border}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
  },
  note: {
    fontSize: '0.8rem',
    color: cockpitTokens.color.textMuted,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  noteButton: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    fontSize: '0.8rem',
    textAlign: 'left' as const,
    cursor: 'pointer',
    '&:hover': {
      color: cockpitTokens.color.text,
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.borderHover}`,
      outlineOffset: 2,
    },
  },
}));

const iconOf: Record<AttentionTone, ReactNode> = {
  failure: <CancelOutlinedIcon />,
  review: <CallMergeIcon />,
  defect: <ReportProblemOutlinedIcon />,
};

/** How each source is named when the card says it is still asking it. */
const sourceName: Record<AttentionSource, string> = {
  builds: 'builds',
  reviews: 'reviews',
  catalog: 'catalog entries',
};

const listOf = (words: string[]) =>
  words.length <= 1
    ? words.join('')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

const Action = ({ action }: { action: AttentionAction }) => {
  const classes = useStyles();
  const { showReviews } = usePullRequestLists();
  switch (action.kind) {
    case 'external':
      return (
        <a
          href={action.href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.action}
        >
          {action.label}
        </a>
      );
    case 'internal':
      return (
        <Link to={action.to} className={classes.action}>
          {action.label}
        </Link>
      );
    case 'in-page':
      return (
        <button
          type="button"
          className={classes.action}
          onClick={() =>
            action.target === 'reviews'
              ? showReviews()
              : scrollToAnchor(CI_WIDGET_ANCHOR_ID)
          }
        >
          {action.label}
        </button>
      );
    default:
      return null;
  }
};

const Row = ({ row }: { row: AttentionRow }) => {
  const classes = useStyles();
  return (
    <li className={classes.row} data-source={row.source}>
      <span className={`${classes.icon} ${classes[row.tone]}`} aria-hidden>
        {iconOf[row.tone]}
      </span>
      <div className={classes.body}>
        <Typography component="div" className={classes.rowTitle}>
          {row.title.map((part, i) =>
            typeof part === 'string' ? (
              <span key={i}>{part}</span>
            ) : (
              <code key={i} className={classes.code}>
                {part.code}
              </code>
            ),
          )}
        </Typography>
        <Typography
          component="div"
          className={classes.detail}
          title={row.detail}
        >
          {row.detail}
        </Typography>
      </div>
      <div className={classes.side}>
        {row.when && (
          <span className={classes.when} title={row.when.exact}>
            {row.when.label}
          </span>
        )}
        <Action action={row.action} />
      </div>
    </li>
  );
};

export const NeedsAttention = () => {
  const classes = useStyles();
  const { reader, owned, unresolved, ci } = useHomeSources();
  const { reviews } = usePullRequestLists();
  const { rows, pending, unchecked, elsewhere } = attentionOf({
    reader,
    owned,
    unresolved,
    ci,
    reviews,
  });

  const settled = pending.length === 0;
  // The open items, not the rows: a row summarising nineteen failing builds
  // counts as nineteen, so the figure agrees with CI health below.
  const openItems = rows.reduce((sum, row) => sum + row.count, 0);
  // Calm only when every source answered and none has anything: a source that
  // could not be asked is not a source with nothing open, and "nothing needs
  // you" over a list that could not be checked is the one wrong answer a reader
  // would act on without checking (specs/needs-attention.md AC-10).
  const calm = settled && rows.length === 0 && unchecked.length === 0;
  const inTeam = reader.data?.inTeam;

  return (
    <section className={classes.card} aria-labelledby="needs-attention-title">
      <div className={classes.head}>
        <h2 id="needs-attention-title" className={classes.title}>
          Needs attention
          {openItems > 0 && (
            <span className={classes.count} data-testid="attention-count">
              {openItems}
            </span>
          )}
        </h2>
        <span className={classes.sources}>From CI, catalog and GitHub</span>
      </div>

      {rows.length > 0 && (
        <ul className={classes.list} aria-label="Needs attention">
          {rows.map(row => (
            <Row key={row.id} row={row} />
          ))}
        </ul>
      )}

      {calm && (
        <div className={classes.calm} data-state="calm">
          <CheckCircleOutlineIcon className={classes.calmIcon} />
          <div>
            <Typography className={classes.calmTitle}>
              Nothing needs your attention right now
            </Typography>
            <Typography className={classes.calmText}>
              Checked{' '}
              {inTeam
                ? "the default branches of your team's repositories"
                : "the default branches of all teams' repositories"}
              , the reviews waiting on you and your components in the catalog.
            </Typography>
          </div>
        </div>
      )}

      {settled && rows.length === 0 && unchecked.length > 0 && (
        <div className={classes.calm} data-state="incomplete">
          <div>
            <Typography className={classes.calmTitle}>
              Nothing found in what could be checked
            </Typography>
            <Typography className={classes.calmText}>
              Some sources could not be asked, so this is not the whole picture.
            </Typography>
          </div>
        </div>
      )}

      {(pending.length > 0 || unchecked.length > 0 || elsewhere > 0) && (
        <div className={classes.notes}>
          {pending.length > 0 && (
            <span className={classes.note} data-state="pending">
              <CircularProgress size={12} />
              Still checking {listOf(pending.map(p => sourceName[p]))}…
            </span>
          )}
          {unchecked.map(u => (
            <span
              key={`${u.source}:${u.what}`}
              className={classes.note}
              data-state="unchecked"
            >
              Couldn't check {u.what} — {u.reason}.
            </span>
          ))}
          {/* The builds CI health marks red and this card does not list,
              because they are not the team's — said, so the two agree. */}
          {elsewhere > 0 && (
            <button
              type="button"
              className={`${classes.note} ${classes.noteButton}`}
              data-state="elsewhere"
              onClick={() => scrollToAnchor(CI_WIDGET_ANCHOR_ID)}
            >
              {elsewhere === 1
                ? "1 other repository fails — not your team's."
                : `${elsewhere} other repositories fail — none of them your team's.`}
            </button>
          )}
        </div>
      )}
    </section>
  );
};
